/**
 * Ф.И.О. преподавателя у участника «Астана дарыны»: базовое значение с регистрации
 * и отдельные значения под каждый диплом.
 *
 * Контракт:
 *  - всё лежит в additional пользователя плоскими ключами: базовое —
 *    astana_daryn_teacher_name, значение под диплом —
 *    astana_daryn_teacher_name_cert_{id}, где id — запись из GET /user/certificates.
 *    Вложенного объекта намеренно нет: additional по всему проекту хранит скаляры,
 *    и плоский ключ заодно попадает под признак isAstanaDarynProfile;
 *  - базовый ключ перестаёт быть значением с регистрации, как только диплом первый
 *    раз подставил в него своё Ф.И.О. Поэтому регистрационное значение при первой
 *    подстановке закрепляется в astana_daryn_teacher_name_origin: оно и
 *    показывается в профиле, и уходит в дипломы, у которых своего Ф.И.О. нет;
 *  - ключ под диплом пишется один раз: пока его нет в загруженном профиле, строка
 *    открыта, после сохранения — закрыта навсегда;
 *  - PDF диплома рендерит бэкенд и берёт только базовый ключ, других источников у
 *    него нет. Поэтому перед скачиванием базовый ключ подменяется значением этого
 *    диплома (useApplyCertificateTeacher), и ручной цикл «поменял Ф.И.О. →
 *    скачал → вернул обратно» больше не нужен;
 *  - "none" в базовом ключе — заглушка участников педолимпа: они сами учителя,
 *    и строки преподавателя у них нет вовсе.
 */

export type Additional = Record<string, unknown>;

export const TEACHER_NAME_KEY = "astana_daryn_teacher_name";
export const TEACHER_ORIGIN_KEY = "astana_daryn_teacher_name_origin";

/* Заглушка, которая стоит в поле преподавателя у участников педолимпа. */
const NO_TEACHER = "none";

export const readString = (
  additional: Additional,
  key: string
): string | null => {
  const value = additional[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
};

export const certificateTeacherKey = (certificateId: number): string =>
  `${TEACHER_NAME_KEY}_cert_${certificateId}`;

export const isAstanaDarynProfile = (additional: unknown): boolean =>
  !!additional &&
  typeof additional === "object" &&
  Object.keys(additional as Additional).some((key) =>
    key.startsWith("astana_daryn_")
  );

const named = (value: string | null): string | null =>
  value && value.toLowerCase() !== NO_TEACHER ? value : null;

/** Ф.И.О. с регистрации; null у педолимпа и пока поле пустое. */
export const readTeacherName = (additional: Additional): string | null =>
  named(readString(additional, TEACHER_ORIGIN_KEY)) ??
  named(readString(additional, TEACHER_NAME_KEY));

/** Ф.И.О., которое уйдёт в этот диплом: своё, а если его не задавали — базовое. */
export const effectiveCertificateTeacherName = (
  additional: Additional,
  certificateId: number
): string | null =>
  readString(additional, certificateTeacherKey(certificateId)) ??
  readTeacherName(additional);
